
import React, { useState } from 'react';
import { 
  LayoutDashboard, 
  Package, 
  Settings, 
  Plus, 
  Trash2, 
  Save, 
  LogOut, 
  User, 
  Lock,
  ChevronRight,
  PlusCircle,
  X,
  Loader2,
  Database,
  CloudUpload,
  CheckCircle2
} from 'lucide-react';
import { useApp } from '../../AppContext';
import { Product } from '../../types';
import { PRODUCTS as INITIAL_PRODUCTS } from '../../constants';
import { supabase } from '../../lib/supabase';

const AdminPanel: React.FC = () => {
  const { products, setProducts, siteSettings, setSiteSettings, setIsAdminMode, refreshData } = useApp();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'dashboard' | 'products' | 'settings'>('dashboard');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [newProduct, setNewProduct] = useState<Partial<Product>>({
    name: '',
    description: '',
    price: 0,
    category: 'Home & Kitchen',
    stock: 20,
    images: ['https://picsum.photos/seed/new/800/800'],
    specs: { 'Material': 'Premium', 'Warranty': '1 Year' }
  });

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (username === 'Admin' && password === 'Admin123') {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Invalid credentials. Access Denied.');
    }
  };

  const showSyncMessage = (msg: string) => {
    setSyncMessage(msg);
    setTimeout(() => setSyncMessage(null), 3000);
  };

  const seedDatabase = async () => {
    if (!window.confirm("This will upload all demo products to your Supabase database. Continue?")) return;
    
    setIsSyncing(true);
    try {
      const { error } = await supabase.from('products').insert(INITIAL_PRODUCTS);
      if (error) throw error;
      await refreshData();
      showSyncMessage("Database seeded successfully!");
    } catch (err: any) {
      alert("Seeding failed: " + err.message);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    setIsSyncing(true);
    try {
      const { error } = await supabase.from('products').delete().eq('id', id);
      if (error) throw error;
      setProducts(prev => prev.filter(p => p.id !== id));
      showSyncMessage("Product deleted.");
    } catch (err: any) {
      alert("Delete failed: " + err.message);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSyncing(true);
    const productToAdd: Product = {
      ...newProduct as Product,
      id: `p${Date.now()}`,
      rating: 5,
      reviews: 0,
      reviewsList: []
    };

    try {
      const { error } = await supabase.from('products').insert([productToAdd]);
      if (error) throw error;
      setProducts(prev => [...prev, productToAdd]);
      setIsAddingProduct(false);
      showSyncMessage("Product published live.");
    } catch (err: any) {
      alert("Publish failed: " + err.message);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSyncing(true);
    try {
      // In Supabase site_settings, we usually just have one row (id=1)
      const { error } = await supabase.from('site_settings').upsert([{ ...siteSettings, id: 1 }]);
      if (error) throw error;
      showSyncMessage("Settings updated globally.");
    } catch (err: any) {
      alert("Settings update failed: " + err.message);
    } finally {
      setIsSyncing(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="w-full max-w-md bg-slate-900 border border-slate-800 p-8 rounded-[2rem] shadow-2xl">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white font-serif italic mb-2">Admin Control</h1>
            <p className="text-slate-400">Enter credentials to manage Do & Get</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Username</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input 
                  type="text" 
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white outline-none focus:ring-2 focus:ring-indigo-600 transition-all"
                  placeholder="Admin"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input 
                  type="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white outline-none focus:ring-2 focus:ring-indigo-600 transition-all"
                  placeholder="••••••••"
                />
              </div>
            </div>
            {error && <p className="text-rose-500 text-sm font-medium">{error}</p>}
            <button type="submit" className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-indigo-900/20">
              Unlock Dashboard
            </button>
            <button 
              type="button" 
              onClick={() => setIsAdminMode(false)}
              className="w-full text-slate-500 text-sm hover:text-white transition-colors"
            >
              Back to Storefront
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Toast Notification */}
      {syncMessage && (
        <div className="fixed top-8 left-1/2 -translate-x-1/2 z-[1000] bg-emerald-600 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-bold text-sm">{syncMessage}</span>
        </div>
      )}

      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col fixed inset-y-0 left-0 z-50">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <span className="bg-indigo-600 p-1.5 rounded-lg text-white font-bold">D&G</span>
            <span className="text-xl font-bold font-serif italic">Control Panel</span>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          <button 
            onClick={() => setActiveTab('dashboard')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'dashboard' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
          >
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </button>
          <button 
            onClick={() => setActiveTab('products')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'products' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
          >
            <Package className="w-5 h-5" />
            Products
          </button>
          <button 
            onClick={() => setActiveTab('settings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'settings' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
          >
            <Settings className="w-5 h-5" />
            Site Settings
          </button>
        </nav>
        <div className="p-4 border-t border-slate-800">
          <button 
            onClick={() => setIsAdminMode(false)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-rose-900/20 hover:text-rose-400 transition-all"
          >
            <LogOut className="w-5 h-5" />
            Exit Admin
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8 relative">
        {isSyncing && (
          <div className="absolute inset-0 bg-white/20 backdrop-blur-[1px] z-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-3xl shadow-2xl flex flex-col items-center gap-4 border border-slate-100">
              <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
              <p className="font-bold text-slate-800">Syncing with Cloud...</p>
            </div>
          </div>
        )}

        <header className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold text-slate-800 capitalize">{activeTab}</h2>
            <p className="text-slate-400 text-sm">Managing live storefront data</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full border border-emerald-100">
              <Database className="w-3 h-3" />
              <span className="text-[10px] font-bold uppercase tracking-wider">Cloud Live</span>
            </div>
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold">A</div>
          </div>
        </header>

        {activeTab === 'dashboard' && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <p className="text-slate-400 text-xs font-bold uppercase mb-2">Total Products</p>
                <h3 className="text-4xl font-bold text-slate-800">{products.length}</h3>
                <div className="mt-4 flex items-center text-emerald-500 text-sm font-bold">
                  <ChevronRight className="w-4 h-4" /> Live in Supabase
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <p className="text-slate-400 text-xs font-bold uppercase mb-2">Free Shipping</p>
                <h3 className="text-3xl font-bold text-slate-800">${siteSettings.freeShippingThreshold}</h3>
                <div className="mt-4 text-slate-400 text-sm">Minimum order amount</div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <p className="text-slate-400 text-xs font-bold uppercase mb-2">Store Name</p>
                <h3 className="text-2xl font-bold text-slate-800 truncate">{siteSettings.siteName}</h3>
                <div className="mt-4 text-slate-400 text-sm">Active branding</div>
              </div>
            </div>

            {products.length === 0 && (
              <div className="bg-indigo-50 border border-indigo-100 p-8 rounded-[2rem] text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm text-indigo-600">
                  <CloudUpload className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-indigo-900 mb-2">Database is Empty</h3>
                <p className="text-indigo-600/70 mb-6 max-w-md mx-auto">
                  Populate your Supabase "products" table with our beautiful demo collections to get started instantly.
                </p>
                <button 
                  onClick={seedDatabase}
                  className="bg-indigo-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200"
                >
                  Seed Initial Data
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'products' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold">Catalog Management</h3>
              <div className="flex gap-3">
                <button 
                  onClick={seedDatabase}
                  className="flex items-center gap-2 bg-slate-100 text-slate-600 px-6 py-2 rounded-xl font-bold hover:bg-slate-200 transition-all text-sm"
                >
                  <Database className="w-4 h-4" /> Seed Demos
                </button>
                <button 
                  onClick={() => setIsAddingProduct(true)}
                  className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-2 rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200"
                >
                  <Plus className="w-4 h-4" /> Add Product
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-slate-400">Product</th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-slate-400">Category</th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-slate-400">Price</th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-slate-400">Stock</th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-slate-400 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {products.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img src={p.images[0]} className="w-10 h-10 rounded-lg object-cover" />
                          <span className="font-bold text-slate-800">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">{p.category}</td>
                      <td className="px-6 py-4 font-bold text-indigo-600">${p.price.toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-lg text-xs font-bold ${p.stock > 10 ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
                          {p.stock} units
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button 
                          disabled={isSyncing}
                          onClick={() => handleDeleteProduct(p.id)}
                          className="p-2 text-slate-400 hover:text-rose-500 transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {products.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-20 text-center text-slate-400 italic">
                        No products found in cloud database.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-3xl bg-white p-10 rounded-[2rem] shadow-sm border border-slate-100 animate-in fade-in slide-in-from-bottom-4">
            <h3 className="text-2xl font-bold mb-8 flex items-center gap-2">
              <Settings className="w-6 h-6 text-indigo-600" />
              Store Configuration
            </h3>
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Site Branding Name</label>
                  <input 
                    type="text" 
                    value={siteSettings.siteName}
                    onChange={(e) => setSiteSettings({...siteSettings, siteName: e.target.value})}
                    className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Free Shipping Min Order ($)</label>
                  <input 
                    type="number" 
                    value={siteSettings.freeShippingThreshold}
                    onChange={(e) => setSiteSettings({...siteSettings, freeShippingThreshold: Number(e.target.value)})}
                    className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600 transition-all"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Support Email</label>
                <input 
                  type="email" 
                  value={siteSettings.contactEmail}
                  onChange={(e) => setSiteSettings({...siteSettings, contactEmail: e.target.value})}
                  className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Physical Store/Office Address</label>
                <textarea 
                  value={siteSettings.address}
                  onChange={(e) => setSiteSettings({...siteSettings, address: e.target.value})}
                  className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600 h-28 transition-all resize-none"
                />
              </div>
              <div className="pt-4 border-t border-slate-100 flex justify-end">
                <button 
                  onClick={handleSaveSettings}
                  disabled={isSyncing}
                  className="flex items-center gap-2 bg-indigo-600 text-white px-10 py-4 rounded-2xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 disabled:opacity-50"
                >
                  <Save className="w-5 h-5" /> {isSyncing ? "Syncing..." : "Update Storefront"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Add Product Modal */}
      {isAddingProduct && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !isSyncing && setIsAddingProduct(false)} />
          <div className="relative bg-white w-full max-w-xl p-10 rounded-[3rem] shadow-2xl animate-in zoom-in fade-in duration-300 overflow-y-auto max-h-[90vh]">
            <button 
              disabled={isSyncing}
              onClick={() => setIsAddingProduct(false)}
              className="absolute top-8 right-8 p-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <h3 className="text-2xl font-bold mb-8">New Store Product</h3>
            <form onSubmit={handleAddProduct} className="space-y-5">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Product Name</label>
                <input 
                  required
                  type="text" 
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                  className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Sale Price ($)</label>
                  <input 
                    required
                    type="number" 
                    step="0.01"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({...newProduct, price: Number(e.target.value)})}
                    className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Inventory</label>
                  <input 
                    required
                    type="number" 
                    value={newProduct.stock}
                    onChange={(e) => setNewProduct({...newProduct, stock: Number(e.target.value)})}
                    className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Catalog Category</label>
                <select 
                  value={newProduct.category}
                  onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                  className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600"
                >
                  <option>Home & Kitchen</option>
                  <option>Grocery & Gourmet</option>
                  <option>Health & Household</option>
                  <option>Office Products</option>
                  <option>Electronics</option>
                  <option>Pet Supplies</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Public Description</label>
                <textarea 
                  required
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                  className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600 h-28 resize-none"
                />
              </div>
              <div className="flex gap-4 pt-4">
                <div className="flex items-center gap-2">
                   <input 
                    type="checkbox" 
                    id="featured"
                    checked={newProduct.isFeatured}
                    onChange={(e) => setNewProduct({...newProduct, isFeatured: e.target.checked})}
                    className="w-4 h-4 text-indigo-600"
                   />
                   <label htmlFor="featured" className="text-sm font-medium">Featured Pick</label>
                </div>
                <div className="flex items-center gap-2">
                   <input 
                    type="checkbox" 
                    id="new"
                    checked={newProduct.isNew}
                    onChange={(e) => setNewProduct({...newProduct, isNew: e.target.checked})}
                    className="w-4 h-4 text-indigo-600"
                   />
                   <label htmlFor="new" className="text-sm font-medium">New Arrival</label>
                </div>
              </div>
              <button 
                type="submit" 
                disabled={isSyncing}
                className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 disabled:opacity-50"
              >
                {isSyncing ? <Loader2 className="animate-spin w-5 h-5" /> : <PlusCircle className="w-6 h-6" />}
                Publish Live to Store
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
